import type { Section } from '@/api/types/core/sections';
import type { CompiledTranslations } from '@/utils/schemaExportUtils';

/**
 * Type de section étendu avec traductions compilées et type d'affichage
 */
export interface SectionWithCompiledTranslations extends Section {
  compiledTitle: string;
  compiledDescription: string;
  displayType: 'normal' | 'dropdown' | 'collapse';
  children?: SectionWithCompiledTranslations[];
  translations?: CompiledTranslations;
}

/**
 * État d'une section dropdown (sélectionnée ou non)
 */
export interface DropdownSectionState {
  sectionId: number;
  isSelected: boolean;
  isVisible: boolean;
}

/**
 * État d'une section collapse (ouverte ou fermée)
 */
export interface CollapseSectionState {
  sectionId: number;
  isCollapsed: boolean;
}

/**
 * Détermine le type d'affichage basé sur le type de section
 * @param sectionType - Type de section ('dropdown', 'collapse', etc.)
 * @returns Type d'affichage pour l'interface
 */
export function getDisplayType(sectionType?: string | null): 'normal' | 'dropdown' | 'collapse' {
  if (!sectionType || sectionType.trim() === '') {
    return 'normal';
  }
  
  const type = sectionType.toLowerCase().trim();
  
  if (type === 'dropdown') {
    return 'dropdown';
  }
  
  if (type === 'collapse') {
    return 'collapse';
  }
  
  return 'normal';
}

/**
 * Extrait le dernier segment d'un nom de section hiérarchique
 * @param sectionName - Nom complet de la section (ex: "HOW.Technic.Details")
 * @returns Dernier segment (ex: "Details")
 */
export function getLastSegment(sectionName: string): string {
  if (!sectionName) return '';
  const segments = sectionName.split('.');
  return segments[segments.length - 1] || '';
}

/**
 * Crée une section avec traductions compilées à partir des données brutes
 * @param section - Section de base
 * @param compiledTranslations - Traductions compilées
 * @param language - Langue à utiliser (défaut: 'fr')
 * @returns Section avec traductions compilées
 */
export function createCompiledSection(
  section: Section,
  compiledTranslations?: CompiledTranslations,
  language: string = 'fr'
): SectionWithCompiledTranslations {
  const translationForLanguage = compiledTranslations?.[language];
  
  return {
    ...section,
    compiledTitle: translationForLanguage?.titre || getLastSegment(section.name),
    compiledDescription: translationForLanguage?.description || '',
    displayType: getDisplayType(section.type),
    translations: compiledTranslations
  };
} 